import {
  getAssistantRuleLabel,
  getAssistantSceneControlLabel,
  getAssistantSceneControlValueLabel,
  getAssistantSceneItemLabel
} from "./assistantSceneState.js";

export type ConversationalAssistantReply = {
  feedback: string;
};

export interface LocalAssistantStateSnapshot {
  sceneId: string;
  currentItemId?: string;
  remainingItemIds?: readonly string[];
  controlValues?: Readonly<Record<string, string | number | boolean>>;
}

function readableStateItem(sceneId: string, itemId: string): string | undefined {
  return getAssistantSceneItemLabel(sceneId, itemId) ?? getAssistantRuleLabel(itemId);
}

/** Build a short, human-readable summary from the same allowlisted IDs sent online. */
export function describeLocalAssistantState(snapshot: LocalAssistantStateSnapshot): string {
  const current = snapshot.currentItemId
    ? readableStateItem(snapshot.sceneId, snapshot.currentItemId)
    : undefined;
  const remaining = [...new Set((snapshot.remainingItemIds ?? [])
    .map((itemId) => readableStateItem(snapshot.sceneId, itemId))
    .filter((label): label is string => Boolean(label) && label !== current))]
    .slice(0, 3);
  const controls = Object.entries(snapshot.controlValues ?? {})
    .filter(([, value]) => value !== false && value !== "")
    .flatMap(([controlId, value]) => {
      const label = getAssistantSceneControlLabel(snapshot.sceneId, controlId);
      if (!label) return [];
      const readableValue = value === true
        ? "是"
        : getAssistantSceneControlValueLabel(snapshot.sceneId, controlId, value);
      return [`${label}：${readableValue}`];
    })
    .slice(0, 3);

  const clauses = [
    current ? `正在处理“${current}”` : "",
    controls.length > 0 ? `当前状态为${controls.join("；")}` : "",
    remaining.length > 0 ? `还待完成${remaining.map((label) => `“${label}”`).join("、")}` : ""
  ].filter(Boolean);
  return clauses.length > 0 ? `我看到你现在${clauses.join("；")}。` : "";
}

const GREETING_PATTERN = /^(?:你好|您好|嗨|哈喽|hi|hello|早上好|下午好|晚上好)[！!。.,，\s]*$/iu;
const IDENTITY_PATTERN = /(?:你是谁|你叫什么|介绍一下你自己|你能做什么)/u;
const PHENOLOGY_SCIENCE_PATTERN = /(?:什么是|解释|介绍|含义|意思|指什么).{0,5}物候学|物候学.{0,5}(?:是什么|含义|意思|研究什么)/u;
const PHENOLOGY_PATTERN = /(?:什么是|解释|含义|意思|指什么).{0,5}物候|物候.{0,5}(?:是什么|含义|意思|指什么)/u;
const NATURE_LANGUAGE_PATTERN = /(?:大自然的语言.{0,8}(?:是什么|指什么|为什么)|为什么.{0,8}大自然的语言)/u;
const ARTICLE_SUMMARY_PATTERN = /(?:课文|文章|《?大自然的语言》?).{0,8}(?:主要讲|主要内容|主旨|中心意思|写了什么)/u;
const FACTOR_PATTERN = /(?:影响|决定).{0,6}物候.{0,8}(?:因素|原因)|物候.{0,8}(?:受什么影响|哪些因素|四个因素)/u;
const PURPOSE_PATTERN = /物候(?:学|资料|观测)?.{0,8}(?:有什么用|用途|作用|意义|为什么重要)/u;

/**
 * 本地模式也先理解常见对话意图，避免网络失败后把所有问题压成同一句任务模板。
 * 这些回答只复述项目内的权威课文要点，不参与判分或状态修改。
 */
export function resolveConversationalAssistantReply(questionValue: string): ConversationalAssistantReply | null {
  const question = questionValue.trim();
  if (GREETING_PATTERN.test(question)) {
    return { feedback: "你好，我是物候学习搭档。你可以直接问课文概念、当前操作，或者把自己的判断发给我一起分析。" };
  }
  if (IDENTITY_PATTERN.test(question)) {
    return { feedback: "我是《大自然的语言》学习旅程中的物候学习搭档。我能结合你当前的场景和操作进度，解释课文概念、梳理证据，也能在任务卡住时给出具体线索。" };
  }
  if (PHENOLOGY_SCIENCE_PATTERN.test(question)) {
    return { feedback: "物候学是一门利用物候知识研究农业生产的科学。它记录植物生长荣枯、动物养育往来等自然现象，据此了解气候随时节的变化及其对动植物的影响。" };
  }
  if (PHENOLOGY_PATTERN.test(question)) {
    return { feedback: "课文所说的物候，是草木荣枯、候鸟去来等与时节和气候变化相关的自然现象。" };
  }
  if (NATURE_LANGUAGE_PATTERN.test(question)) {
    return { feedback: "“大自然的语言”指花开、鸟来、草木荣枯等自然现象传递出的时节与气候信息。劳动人民观察这些信息，并据以安排农事。" };
  }
  if (ARTICLE_SUMMARY_PATTERN.test(question)) {
    return { feedback: "课文先用四季现象说明什么是物候和物候学，再介绍物候观测对农业的价值、影响物候来临的四类因素，以及物候研究的多方面意义。" };
  }
  if (FACTOR_PATTERN.test(question)) {
    return { feedback: "课文列出的主要因素是纬度、经度、高下和古今差异。判断具体案例时，还要看比较对象、季节和适用条件，不能只套用因素名称。" };
  }
  if (PURPOSE_PATTERN.test(question)) {
    return { feedback: "物候资料可以用于预报农时、选择播种日期，也能为农作物区划、造林采种、植物引种、减轻虫害和山区农业调查提供参考。" };
  }
  return null;
}

const TASK_HELP_PATTERN = /(?:怎么|如何|不会|没思路|卡住|帮我|提示|线索|从哪|下一步|怎么答|怎么做|选什么|放哪里|答案)/u;

/** 对明确求助的问题给出自然的起步句，具体线索仍来自当前场景权威目录。 */
export function taskHelpLead(questionValue: string): string {
  return TASK_HELP_PATTERN.test(questionValue.trim())
    ? "可以先从这一条具体线索开始："
    : "结合你现在的操作，先抓住这一点：";
}
